// Lead magnet types — the dashboard's copy of Report Management Service's
// utils/leadMagnetTypes.js.
//
// Copied verbatim, like WIDGET_THEMES: the builder has to show exactly the modules and
// copy the scanner will actually use, and there is no shared build between the two. If
// the backend list changes, change this one.

export // `subject` is the type in a sentence, lower-case: "This dark web exposure lead magnet
// widget is currently unavailable." (routes/Public.js widgetUnavailable).
const LEAD_MAGNET_TYPES = [
  {
    id: 'attack-surface',
    name: 'Attack surface scan',
    subject: 'attack surface',
    input: 'domain',
    modules: ['breach', 'dns', 'email', 'ssl', 'headers', 'infra', 'typosquatting'],
    asksCompany: true,
    blurb: 'Everything we check, in one report.',
    title: 'How exposed is your domain?',
    subtitle: 'Free scan, results in two minutes.',
    buttonText: 'Scan my domain',
    chips: ['Dark Web', 'SSL/TLS', 'Headers', 'DNS', 'Email'],
    reportTitle: 'External Security & Attack Surface Review',
    reportLede: 'The public attack surface of this domain: mail authentication, certificates, headers, DNS, hosting and lookalike domains.',
  },
  {
    id: 'email-security',
    name: 'Email security report',
    subject: 'email security',
    input: 'domain',
    modules: ['email'],
    blurb: 'SPF, DKIM, DMARC, MX and mail-provider configuration, plus any mailboxes you name.',
    title: 'Can anyone send email as you?',
    subtitle: 'Check your SPF, DKIM and DMARC in under a minute. Add mailboxes to check them too.',
    buttonText: 'Check my email security',
    chips: ['SPF', 'DKIM', 'DMARC', 'MX'],
    reportTitle: 'Email Security Review',
    reportLede: 'How this domain protects its mail: SPF, DKIM, DMARC and the providers that send on its behalf.',
    // Optional addresses beside the domain. They feed a Mailbox Exposure category — added
    // only when addresses are supplied, so the type's own module list stays ['email'].
    asksEmail: true,
    emailModule: 'mailbox',
  },
  {
    id: 'website-health',
    name: 'Website health report',
    subject: 'website health',
    input: 'url',
    modules: ['ssl', 'headers'],
    blurb: 'SSL/TLS, security headers, redirects and exposed technologies.',
    title: 'Is your website secure?',
    subtitle: 'Certificates, security headers and redirect handling, checked live.',
    buttonText: 'Check my website',
    chips: ['SSL/TLS', 'Headers', 'Redirects'],
    reportTitle: 'Website Security Review',
    reportLede: 'How this website protects visitors: certificates, HTTPS handling and the security headers the server sends.',
  },
  {
    id: 'dark-web',
    name: 'Dark web exposure report',
    subject: 'dark web exposure',
    input: 'domain',
    modules: ['breach'],
    blurb: 'Breached accounts tied to the domain, plus any staff addresses you name.',
    title: 'Are your staff credentials on the dark web?',
    subtitle: 'Enter your domain, and optionally staff email addresses. We never show passwords.',
    buttonText: 'Check for breaches',
    chips: ['Breaches', 'Exposed accounts'],
    reportTitle: 'Dark Web Exposure Review',
    reportLede: 'Credentials tied to this domain that appear in known public breaches.',
    inputPlaceholder: 'example.com',
    // Optional addresses, checked alongside the domain's shared mailboxes.
    asksEmail: true,
    emailModule: 'breach',
  },
  {
    id: 'brand-impersonation',
    name: 'Brand impersonation report',
    subject: 'brand impersonation',
    input: 'domain',
    // Typosquatting finds the lookalikes; email carries DMARC, which is what decides
    // whether a lookalike can actually send mail as you. The report is thin without both.
    modules: ['typosquatting', 'email'],
    blurb: 'Lookalike domains, typo-squats, and whether DMARC blunts them.',
    title: 'Is someone impersonating your brand?',
    subtitle: 'Lookalike domains registered against you, and how exposed you are to them.',
    buttonText: 'Check for impersonation',
    chips: ['Lookalikes', 'Typo-squats', 'DMARC'],
    reportTitle: 'Brand Impersonation Review',
    reportLede: 'Lookalike and typosquatted domains registered against this brand, and whether mail authentication blunts them.',
  },
  {
    id: 'domain-resilience',
    name: 'Domain resilience report',
    subject: 'domain resilience',
    input: 'domain',
    modules: ['dns', 'ssl'],
    blurb: 'DNS configuration, nameserver redundancy, DNSSEC and certificate expiry.',
    title: 'How resilient is your domain?',
    subtitle: 'DNS, nameservers and certificates — the things that take you offline.',
    buttonText: 'Check my domain',
    chips: ['DNS', 'Nameservers', 'Certificates'],
    reportTitle: 'Domain Resilience Review',
    reportLede: 'How this domain resolves and renews: DNS configuration, nameserver redundancy, DNSSEC and certificate expiry.',
  },
  {
    id: 'privacy-trust',
    name: 'Privacy & trust report',
    subject: 'privacy & trust',
    input: 'url',
    modules: ['headers', 'ssl'],
    blurb: 'Transport security, privacy-related headers and public trust signals.',
    title: 'Does your site look trustworthy?',
    subtitle: 'The signals a visitor’s browser checks before they hand over a form.',
    buttonText: 'Check my site',
    chips: ['HTTPS', 'Headers', 'Trust signals'],
    reportTitle: 'Privacy & Trust Review',
    reportLede: 'Transport security and the privacy-related signals this site gives visitors and their browsers.',
  },
  {
    id: 'public-ip',
    name: 'Public IP report',
    subject: 'public IP',
    input: 'ip-or-domain',
    // The visitor's own address, read by the widget and passed through. Open ports on the
    // connection someone is sitting behind is the most visceral of these reports.
    modules: ['publicIp'],
    asksCompany: true,
    blurb: 'Open ports and exposed services on a domain or public IP.',
    title: 'What is your network exposing?',
    subtitle: 'Enter a domain or public IP and we check it for open services.',
    buttonText: 'Check exposure',
    chips: ['Open ports', 'Exposed services', 'TLS'],
    reportTitle: 'Public IP Exposure Review',
    reportLede: 'What this public address exposes to the internet: open services, ownership and the certificate served on it.',
    inputPlaceholder: 'example.com or 203.0.113.10',
  },
];

export const DEFAULT_TYPE = 'attack-surface';

export const leadMagnetType = (id) =>
  LEAD_MAGNET_TYPES.find((t) => t.id === id) ||
  LEAD_MAGNET_TYPES.find((t) => t.id === DEFAULT_TYPE);
